import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShoppingCart, Mail, Wallet, Trash2, Plus, Pencil, FileText, X } from "lucide-react";
import { toast } from "sonner";

const statusLabels = {
  pending: "En attente", in_progress: "En cours", completed: "Terminé", cancelled: "Annulé"
};
const statusColors = {
  pending: "bg-amber-50 text-amber-600", in_progress: "bg-blue-50 text-blue-600",
  completed: "bg-emerald-50 text-emerald-600", cancelled: "bg-slate-100 text-slate-500"
};

const units = [
  { value: "u", label: "Unité" }, { value: "h", label: "Heure" },
  { value: "j", label: "Jour" }, { value: "m", label: "Mètre" },
  { value: "m2", label: "m²" }, { value: "kg", label: "kg" }, { value: "forf", label: "Forfait" },
];

export default function CommandesPage() {
  const { apiFetch, API, user } = useAuth();
  const isWebmaster = user?.role === "webmaster";
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acompteModal, setAcompteModal] = useState(null);
  const [acompteValue, setAcompteValue] = useState("");
  const [acompteDate, setAcompteDate] = useState("");

  // BC create/edit state
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [clients, setClients] = useState([]);
  const [devisList, setDevisList] = useState([]);
  const [formClientId, setFormClientId] = useState("");
  const [formDevisId, setFormDevisId] = useState("");
  const [formObjet, setFormObjet] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formTaxRate, setFormTaxRate] = useState(0);
  const [formItems, setFormItems] = useState([{ description: "", quantity: 1, unit: "u", unit_price: 0, item_type: "product" }]);
  const [saving, setSaving] = useState(false);

  const fetchCommandes = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/commandes`);
      if (res.ok) setCommandes(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiFetch, API]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/clients`);
      if (res.ok) setClients(await res.json());
    } catch (e) { console.error(e); }
  }, [apiFetch, API]);

  const fetchDevis = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/devis?status=accepted`);
      if (res.ok) setDevisList(await res.json());
    } catch (e) { console.error(e); }
  }, [apiFetch, API]);

  useEffect(() => { fetchCommandes(); }, [fetchCommandes]);

  const openCreateForm = () => {
    setEditingId(null);
    setFormClientId("");
    setFormDevisId("");
    setFormObjet("");
    setFormNotes("");
    setFormTaxRate(0);
    setFormItems([{ description: "", quantity: 1, unit: "u", unit_price: 0, item_type: "product" }]);
    fetchClients();
    fetchDevis();
    setFormOpen(true);
  };

  const openEditForm = async (cmd) => {
    setEditingId(cmd.commande_id);
    setFormClientId(cmd.client_id || "");
    setFormDevisId(cmd.devis_id || "");
    setFormObjet(cmd.objet || "");
    setFormNotes(cmd.notes || "");
    setFormTaxRate(cmd.tax_rate || 0);
    setFormItems(cmd.items?.length > 0 ? cmd.items.map(i => ({
      description: i.description || "", quantity: i.quantity || 1, unit: i.unit || "u",
      unit_price: i.unit_price || 0, item_type: i.item_type || "product"
    })) : [{ description: "", quantity: 1, unit: "u", unit_price: 0, item_type: "product" }]);
    fetchClients();
    fetchDevis();
    setFormOpen(true);
  };

  const handleDevisSelect = (devisId) => {
    setFormDevisId(devisId);
    if (!devisId) return;
    const d = devisList.find(x => x.devis_id === devisId);
    if (d) {
      setFormClientId(d.client_id);
      setFormObjet(d.objet || "");
      setFormTaxRate(d.tax_rate || 0);
      setFormItems(d.items?.map(i => ({
        description: i.description || "", quantity: i.quantity || 0,
        unit: i.unit || "u", unit_price: i.unit_price || 0, item_type: i.item_type || "product"
      })) || []);
    }
  };

  const addItem = () => setFormItems([...formItems, { description: "", quantity: 1, unit: "u", unit_price: 0, item_type: "product" }]);
  const removeItem = (idx) => setFormItems(formItems.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    const updated = [...formItems];
    updated[idx] = { ...updated[idx], [field]: value };
    setFormItems(updated);
  };

  const subtotal = formItems.reduce((sum, i) => {
    if (i.item_type === "title") return sum;
    return sum + (i.quantity || 0) * (i.unit_price || 0);
  }, 0);
  const taxAmount = subtotal * (formTaxRate || 0) / 100;
  const total = subtotal + taxAmount;

  const handleSave = async () => {
    if (!formClientId) { toast.error("Veuillez sélectionner un client"); return; }
    setSaving(true);
    try {
      const payload = {
        client_id: formClientId,
        devis_id: formDevisId || null,
        items: formItems,
        tax_rate: formTaxRate,
        notes: formNotes,
        objet: formObjet
      };
      let res;
      if (editingId) {
        res = await apiFetch(`${API}/commandes/${editingId}`, { method: "PUT", body: JSON.stringify(payload) });
      } else {
        res = await apiFetch(`${API}/commandes`, { method: "POST", body: JSON.stringify(payload) });
      }
      if (res.ok) {
        toast.success(editingId ? "BC modifié" : "BC créé");
        setFormOpen(false);
        fetchCommandes();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur");
      }
    } catch (e) { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const updateStatus = async (commandeId, newStatus) => {
    const res = await apiFetch(`${API}/commandes/${commandeId}/status`, {
      method: "PUT", body: JSON.stringify({ status: newStatus })
    });
    if (res.ok) { toast.success("Statut mis à jour"); fetchCommandes(); }
  };

  const sendBcEmail = async (commandeId) => {
    const res = await apiFetch(`${API}/commandes/${commandeId}/send-email`, { method: "POST" });
    if (res.ok) {
      const data = await res.json();
      toast.success(data.message);
    } else { toast.error("Erreur lors de l'envoi"); }
  };

  const handleDelete = async (commandeId) => {
    if (!window.confirm("ATTENTION : Supprimer ce bon de commande supprimera aussi les factures et interventions liées.\n\nConfirmez-vous la suppression ?")) return;
    const res = await apiFetch(`${API}/commandes/${commandeId}`, { method: "DELETE" });
    if (res.ok) { toast.success("Bon de commande supprimé"); fetchCommandes(); }
    else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Erreur lors de la suppression");
    }
  };

  const openAcompteModal = (commande) => {
    setAcompteModal(commande);
    setAcompteValue(commande.acompte_saisi != null ? String(commande.acompte_saisi) : "");
    setAcompteDate(commande.acompte_date || new Date().toISOString().slice(0, 10));
  };

  const saveAcompte = async () => {
    if (!acompteModal) return;
    const val = parseFloat(acompteValue);
    if (isNaN(val) || val < 0) { toast.error("Montant invalide"); return; }
    const res = await apiFetch(`${API}/commandes/${acompteModal.commande_id}/acompte`, {
      method: "PUT", body: JSON.stringify({ acompte_saisi: val, acompte_date: acompteDate })
    });
    if (res.ok) { toast.success("Acompte enregistré"); setAcompteModal(null); fetchCommandes(); }
    else { toast.error("Erreur lors de la sauvegarde"); }
  };

  const handleDownloadPdf = async (cmd) => {
    try {
      const res = await apiFetch(`${API}/commandes/${cmd.commande_id}/pdf`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${cmd.commande_number.replace(/\//g, "-")}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { toast.error("Erreur"); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="p-4 sm:p-8" data-testid="commandes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-barlow text-2xl font-bold text-slate-900 tracking-tight uppercase">Bons de Commande</h1>
          <p className="text-sm text-slate-500 mt-1">Gérez vos bons de commande manuels ou automatiques</p>
        </div>
        <Button onClick={openCreateForm} className="bg-[#1a3a5c] hover:bg-[#15304d] gap-2" data-testid="create-bc-btn">
          <Plus className="w-4 h-4" /> Créer BC
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Chargement...</div>
          ) : commandes.length === 0 ? (
            <div className="p-12 text-center">
              <ShoppingCart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-400">Aucun bon de commande pour le moment</p>
              <p className="text-xs text-slate-300 mt-1">Créez un BC manuellement ou attendez qu'un devis soit accepté</p>
            </div>
          ) : (
            <div className="overflow-x-auto"><Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold">N. Commande</TableHead>
                  <TableHead className="text-xs font-semibold">N. Devis</TableHead>
                  <TableHead className="text-xs font-semibold">Client</TableHead>
                  <TableHead className="text-xs font-semibold">Date</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Montant</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Acompte</TableHead>
                  <TableHead className="text-xs font-semibold">Statut</TableHead>
                  <TableHead className="text-xs font-semibold w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {commandes.map(c => (
                  <TableRow key={c.commande_id} className="table-row-hover" data-testid={`commande-row-${c.commande_id}`}>
                    <TableCell className="text-sm font-medium text-slate-900">{c.commande_number}</TableCell>
                    <TableCell className="text-sm text-slate-500">{c.devis_number || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{c.client_name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{formatDate(c.created_at)}</TableCell>
                    <TableCell className="text-sm text-right font-semibold text-slate-900">{c.total?.toLocaleString("fr-FR")} EUR</TableCell>
                    <TableCell className="text-right">
                      {c.acompte_saisi != null ? (
                        <Badge className="bg-emerald-50 text-emerald-700 text-xs cursor-pointer hover:bg-emerald-100 border-0" onClick={() => openAcompteModal(c)} data-testid={`acompte-badge-${c.commande_id}`}>
                          {c.acompte_saisi.toLocaleString("fr-FR")} EUR{c.acompte_date ? ` (${new Date(c.acompte_date).toLocaleDateString("fr-FR")})` : ""}
                        </Badge>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => openAcompteModal(c)} className="h-6 px-2 text-xs text-slate-400 hover:text-slate-600" data-testid={`acompte-btn-${c.commande_id}`}>
                          <Wallet className="w-3 h-3 mr-1" /> Saisir
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select value={c.status} onValueChange={(v) => updateStatus(c.commande_id, v)}>
                        <SelectTrigger className="h-7 text-xs w-32 border-0" data-testid={`commande-status-${c.commande_id}`}>
                          <Badge className={`${statusColors[c.status]} text-[11px] font-medium border-0`}>{statusLabels[c.status]}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">En attente</SelectItem>
                          <SelectItem value="in_progress">En cours</SelectItem>
                          <SelectItem value="completed">Terminé</SelectItem>
                          <SelectItem value="cancelled">Annulé</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditForm(c)} className="h-7 w-7 p-0 text-slate-500 hover:text-slate-700" title="Modifier" data-testid={`edit-bc-${c.commande_id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(c)} className="h-7 w-7 p-0 text-slate-500 hover:text-slate-700" title="Télécharger PDF" data-testid={`pdf-bc-${c.commande_id}`}>
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => sendBcEmail(c.commande_id)} className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700" title="Envoyer par email" data-testid={`send-bc-email-${c.commande_id}`}>
                          <Mail className="w-4 h-4" />
                        </Button>
                        {isWebmaster && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(c.commande_id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600" title="Supprimer" data-testid={`delete-bc-${c.commande_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></div>
          )}
        </CardContent>
      </Card>

      {/* Modal Acompte */}
      <Dialog open={!!acompteModal} onOpenChange={(open) => { if (!open) setAcompteModal(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="acompte-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">
              Saisir l'acompte - {acompteModal?.commande_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm text-slate-500 mb-1">Client: <strong>{acompteModal?.client_name}</strong></p>
              <p className="text-sm text-slate-500">Total BC: <strong>{acompteModal?.total?.toLocaleString("fr-FR")} EUR</strong></p>
            </div>
            <div>
              <Label>Montant de l'acompte reçu (EUR)</Label>
              <Input type="number" step="0.01" min="0" value={acompteValue} onChange={(e) => setAcompteValue(e.target.value)} placeholder="Ex: 1500.00" data-testid="acompte-input" />
            </div>
            <div>
              <Label>Date du versement</Label>
              <Input type="date" value={acompteDate} onChange={(e) => setAcompteDate(e.target.value)} data-testid="acompte-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcompteModal(null)} data-testid="acompte-cancel-btn">Annuler</Button>
            <Button onClick={saveAcompte} className="bg-[#1a3a5c] hover:bg-[#15304d]" data-testid="acompte-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Create/Edit BC */}
      <Dialog open={formOpen} onOpenChange={(open) => { if (!open) setFormOpen(false); }}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bc-form-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-slate-900">
              {editingId ? "Modifier le Bon de Commande" : "Créer un Bon de Commande"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Client + Devis link */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Client *</Label>
                <Select value={formClientId} onValueChange={setFormClientId}>
                  <SelectTrigger data-testid="bc-client-select"><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Lier un devis (optionnel)</Label>
                <Select value={formDevisId} onValueChange={handleDevisSelect}>
                  <SelectTrigger data-testid="bc-devis-select"><SelectValue placeholder="Aucun devis" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {devisList.map(d => (
                      <SelectItem key={d.devis_id} value={d.devis_id}>{d.devis_number} - {d.client_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Objet</Label>
              <Input value={formObjet} onChange={e => setFormObjet(e.target.value)} placeholder="Objet du bon de commande" data-testid="bc-objet-input" />
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-slate-500 uppercase">Lignes</Label>
                <Button size="sm" variant="outline" onClick={addItem} className="h-7 text-xs gap-1" data-testid="bc-add-item-btn">
                  <Plus className="w-3 h-3" /> Ajouter
                </Button>
              </div>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-[10px] font-semibold w-[40%]">Description</TableHead>
                      <TableHead className="text-[10px] font-semibold text-center w-16">Qté</TableHead>
                      <TableHead className="text-[10px] font-semibold text-center w-20">Unité</TableHead>
                      <TableHead className="text-[10px] font-semibold text-right w-24">P.U. HT</TableHead>
                      <TableHead className="text-[10px] font-semibold text-right w-24">Total</TableHead>
                      <TableHead className="w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formItems.map((item, idx) => {
                      const lineTotal = (item.item_type === "title") ? 0 : (item.quantity || 0) * (item.unit_price || 0);
                      return (
                        <TableRow key={idx}>
                          <TableCell className="p-1">
                            <Input value={item.description} onChange={e => updateItem(idx, "description", e.target.value)} placeholder="Description" className="h-8 text-sm" data-testid={`bc-item-desc-${idx}`} />
                          </TableCell>
                          <TableCell className="p-1">
                            <Input type="number" value={item.quantity} onChange={e => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)} className="h-8 text-sm text-center" data-testid={`bc-item-qty-${idx}`} />
                          </TableCell>
                          <TableCell className="p-1">
                            <Select value={item.unit} onValueChange={v => updateItem(idx, "unit", v)}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {units.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="p-1">
                            <Input type="number" step="0.01" value={item.unit_price} onChange={e => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} className="h-8 text-sm text-right" data-testid={`bc-item-price-${idx}`} />
                          </TableCell>
                          <TableCell className="text-sm text-right font-medium p-1">{lineTotal.toFixed(2)}</TableCell>
                          <TableCell className="p-1">
                            {formItems.length > 1 && (
                              <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600">
                                <X className="w-3 h-3" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Tax & Totals */}
            <div className="flex items-end justify-between gap-4">
              <div className="w-32">
                <Label className="text-xs font-semibold text-slate-500 uppercase">TVA %</Label>
                <Input type="number" step="0.1" value={formTaxRate} onChange={e => setFormTaxRate(parseFloat(e.target.value) || 0)} className="h-8 text-sm" data-testid="bc-tax-rate" />
              </div>
              <div className="text-right space-y-1">
                <p className="text-sm text-slate-500">Sous-total: <strong>{subtotal.toFixed(2)} EUR</strong></p>
                {formTaxRate > 0 && <p className="text-sm text-slate-500">TVA ({formTaxRate}%): <strong>{taxAmount.toFixed(2)} EUR</strong></p>}
                <p className="text-lg font-bold text-slate-900">{total.toFixed(2)} EUR</p>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Notes</Label>
              <Textarea value={formNotes} onChange={e => setFormNotes(e.target.value)} placeholder="Notes internes ou remarques" rows={2} data-testid="bc-notes" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1a3a5c] hover:bg-[#15304d]" data-testid="bc-save-btn">
              {saving ? "Enregistrement..." : editingId ? "Modifier" : "Créer le BC"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
